/**
 * Prepara en el CLIENTE la foto que se va a subir: recorte cuadrado centrado y
 * reescalado.
 *
 * No sustituye a la normalización del servidor —que es la que manda, borra el
 * EXIF y garantiza el formato—, sino que evita subir 5 MB desde el móvil para
 * que el servidor los tire. Una foto de 12 Mpx pasa de ~4 MB a ~40 KB antes de
 * salir del dispositivo.
 *
 * Si algo falla (navegador sin canvas, imagen que no decodifica) devuelve el
 * fichero ORIGINAL: el servidor lo normalizará igual. Preparar es una
 * optimización, no un requisito.
 */

/** Lado del cuadrado que se sube. Mayor que los 256 del servidor, para que el
 *  reescalado final lo haga él con un filtro mejor y no se pierda nitidez. */
export const CLIENT_AVATAR_SIZE = 512;

/** Tamaño máximo que el servidor acepta (`MAX_INPUT_BYTES` en Rust). */
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/** Tipos que el servidor sabe decodificar. */
export const ACCEPTED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export type AvatarFileError = "too-large" | "unsupported-type";

/**
 * Comprueba lo que se puede comprobar sin decodificar.
 *
 * Es solo para dar un mensaje ÚTIL antes de subir: el `type` de un `File` lo
 * pone el navegador a partir de la extensión y se puede falsear, así que la
 * validación de verdad (magic bytes) la hace el servidor.
 */
export function validateAvatarFile(file: File): AvatarFileError | null {
  if (file.size > MAX_AVATAR_BYTES) {
    return "too-large";
  }
  if (file.type && !ACCEPTED_AVATAR_TYPES.includes(file.type)) {
    return "unsupported-type";
  }
  return null;
}

/** Recorte cuadrado centrado: el cuadro más grande que cabe en la imagen. */
export function centerSquare(width: number, height: number) {
  const side = Math.min(width, height);
  return {
    side,
    x: Math.floor((width - side) / 2),
    y: Math.floor((height - side) / 2),
  };
}

/**
 * Devuelve el fichero listo para subir. Nunca lanza: ante cualquier problema
 * devuelve el original.
 */
export async function prepareAvatarFile(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const { side, x, y } = centerSquare(bitmap.width, bitmap.height);

    const canvas = document.createElement("canvas");
    canvas.width = CLIENT_AVATAR_SIZE;
    canvas.height = CLIENT_AVATAR_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return file;
    }
    ctx.drawImage(bitmap, x, y, side, side, 0, 0, CLIENT_AVATAR_SIZE, CLIENT_AVATAR_SIZE);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/webp", 0.9);
    });
    if (!blob || blob.size === 0) {
      return file;
    }
    return new File([blob], "avatar.webp", { type: "image/webp" });
  } catch {
    // Navegador sin `createImageBitmap`, imagen corrupta, canvas «tainted»…
    // El servidor normaliza igual; subir el original es peor pero funciona.
    return file;
  }
}
